"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Paper,
  Group,
  Badge,
  Anchor,
  Box,
  Button,
  Flex,
} from "@mantine/core";
import { colorSeasons } from "@/constants";
import Link from "next/link";
import Image from "next/image";
import { IconShirt, IconArrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";

const formatName = (name: string) => {
  return name
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join("");
};

export default function ColorPaletteDirectoryContent() {
  const t = useTranslations("colorPalette");

  return (
    <>
      <Stack gap="xl">
        <div>
          <Title order={1} mb="md">
            {t("title")}
          </Title>
          <Text size="lg" c="dimmed">
            {t("description")}
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          {Object.entries(colorSeasons).map(([season, seasonData]) => (
            <Paper key={season} radius="md" p="md">
              <Stack gap="md">
                <Anchor component={Link} href={`/color-palette/${season.toLowerCase()}`} underline="never" c="inherit">
                  <Title order={2}>{formatName(season)}</Title>
                </Anchor>
                <Text c="dimmed">
                  {t("hasPalettes", { season: formatName(season), count: Object.keys(seasonData.subseasons).length })}{" "}
                  {Object.entries(seasonData.subseasons).map(([subSeason, subSeasonData], index) => {
                    return (
                      <span key={subSeason}>
                        {subSeason
                          .split("_")
                          .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
                          .join(" ")}
                        {index < Object.keys(seasonData.subseasons).length - 1 ? ", " : "."}
                      </span>
                    );
                  })}
                </Text>

                {/* Season Image */}
                <Box
                  mih={{ base: "200px", md: "300px" }}
                  style={{
                    flex: 1,
                    minWidth: "320px",
                    position: "relative",
                    borderRadius: "1rem",
                  }}
                >
                  <Image
                    src={`/${season.toUpperCase()}.webp`}
                    alt={`${formatName(season)} season`}
                    fill
                    priority
                    fetchPriority="high"
                    quality={85}
                    style={{
                      borderRadius: "1rem",
                      objectFit: "contain",
                      objectPosition: "center",
                    }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="eager"
                    decoding="async"
                  />
                </Box>

                <Stack gap="md">
                  {Object.entries(seasonData.subseasons).map(([subSeason, subSeasonData]) => (
                    <Link
                      key={subSeason}
                      href={`/color-palette/${season.toLowerCase()}/${subSeason.toLowerCase().replace(/_/g, "-")}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Paper withBorder p="md" radius="md" style={{ cursor: "pointer" }}>
                        <Group justify="space-between" mb="xs">
                          <Group gap="xs">
                            <Text size="xl">{subSeasonData.emoji}</Text>
                            <Text fw="bold">
                              {subSeason
                                .split("_")
                                .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
                                .join(" ")}{" "}
                              {season.charAt(0) + season.slice(1).toLowerCase()}
                            </Text>
                          </Group>
                        </Group>
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {subSeasonData.description}
                        </Text>
                        <Text size="sm" mt="xs" c="blue">
                          {t("viewPalette", { subSeason: formatName(subSeason), season: formatName(season) })}
                        </Text>
                      </Paper>
                    </Link>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>

        <Paper withBorder p="lg" radius="md" bg="blue.0">
          <Group justify="space-between" wrap="wrap" gap="md">
            <Box style={{ flex: 1, minWidth: 240 }}>
              <Title order={2} fz="lg" mb={4}>
                Compare seasons side by side
              </Title>
              <Text c="dimmed" size="sm">
                Not sure which season fits? See detailed comparisons between any two color palettes
                — Bright Spring vs True Spring, Cool Winter vs Deep Winter, and more.
              </Text>
            </Box>
            <Button
              component={Link}
              href="/color-palette/comparison"
              rightSection={<IconArrowRight size={16} />}
              variant="filled"
              color="blue"
            >
              Browse comparisons
            </Button>
          </Group>
        </Paper>
      </Stack>
    </>
  );
}
